import Editor, { DiffEditor } from '@monaco-editor/react';
import {
  LoaderCircle,
  Save,
  X,
  AlertTriangle,
  Check,
  GitCompare,
  ClipboardList,
  FileSearch,
  ArrowRight,
  ArrowLeft,
  ClipboardCopy,
} from 'lucide-react';
import { resolveMonacoTheme, registerMonacoThemes } from '../../utils/monacoThemes';
import { createPortal } from 'react-dom';
import { ConfirmModal } from '../ui/Modal';
import { languageForName } from './fileEditLanguage';

/** The remote file editor markup: header, editor or diff, conflict and discard dialogs. Plain function called during render, so the element tree is unchanged. */
export function renderFileEditModal({
  t,
  compare,
  applyHunk,
  path,
  name,
  onClose,
  local,
  content,
  conflict,
  isDark,
  loading,
  mode,
  loadError,
  setContent,
  save,
  saving,
  saveError,
  savedFlash,
  setMode,
  setConflict,
  latest,
  confirmDiscard,
  setConfirmDiscard,
  confirmDropLeft,
  setConfirmDropLeft,
  compareMenu,
  setCompareMenu,
  compareError,
  hunks,
  setHunks,
  compareContent,
  compareDirty,
  copied,
  diffEditorRef,
  dirty,
  saveRef,
  contentRef,
  diffModifiedRef,
  detachDiffModels,
  doWrite,
  startCompare,
  endCompare,
  applyHunkReverseAction,
  copyCompareResult,
  openDiff,
  requestOpenDiff,
  saveDiffResult,
  requestClose,
}) {
  const bindSaveShortcut = (editor, monaco) => {
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => saveRef.current());
  };

  const language = languageForName(name);
  const monacoTheme = resolveMonacoTheme(isDark);
  const editorOptions = {
    minimap: { enabled: false },
    fontSize: 12.5,
    scrollBeyondLastLine: false,
    automaticLayout: true,
    renderWhitespace: 'none',
    wordWrap: 'off',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 modal-backdrop" onClick={requestClose} />
      <div className="relative mx-4 modal-content file-edit-modal">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-inner">
            <div>
              <h3 className="modal-header-title">
                {name}
                {dirty && <span className="file-edit-dirty-dot" title={t('fileEdit.unsaved', 'Unsaved changes')} />}
              </h3>
              <p className="modal-header-subtitle">
                {mode === 'diff'
                  ? local
                    ? t('fileEdit.diffSubtitleLocal', 'Version on disk (left) vs. yours (right, editable)')
                    : t('fileEdit.diffSubtitle', 'Server version (left) vs. yours (right, editable)')
                  : path}
              </p>
            </div>
          </div>
          <div className="file-edit-header-actions">
            {savedFlash && (
              <span className="file-edit-saved-flash">
                <Check size={13} /> {t('fileEdit.saved', 'Saved')}
              </span>
            )}
            {mode === 'edit' ? (
              <button
                type="button"
                className="settings-action-btn"
                onClick={save}
                disabled={saving || loading || !!loadError || !dirty}
              >
                {saving ? <LoaderCircle size={13} className="animate-spin" /> : <Save size={13} />}
                {t('fileEdit.save', 'Save')}
              </button>
            ) : null}
            {mode === 'edit' && (
              <div className="file-edit-compare-wrap">
                <button
                  type="button"
                  className="settings-action-btn"
                  onClick={() => setCompareMenu((v) => !v)}
                  disabled={loading || !!loadError}
                  aria-haspopup="true"
                  aria-expanded={compareMenu}
                >
                  <GitCompare size={13} />
                  {t('fileEdit.compare', 'Compare')}
                </button>
                {compareMenu && (
                  <div className="file-edit-compare-menu" role="menu">
                    <button type="button" role="menuitem" onClick={() => startCompare('clipboard')}>
                      <ClipboardList size={13} />
                      {t('fileEdit.compareClipboard', 'With clipboard')}
                    </button>
                    <button type="button" role="menuitem" onClick={() => startCompare('file')}>
                      <FileSearch size={13} />
                      {t('fileEdit.compareFile', 'With a local file…')}
                    </button>
                  </div>
                )}
              </div>
            )}
            {mode === 'edit' ? null : (
              <>
                <button
                  type="button"
                  className="settings-action-btn"
                  onClick={saveDiffResult}
                  disabled={saving}
                >
                  {saving ? <LoaderCircle size={13} className="animate-spin" /> : <Save size={13} />}
                  {t('fileEdit.saveMerged', 'Save my version')}
                </button>
                <button
                  type="button"
                  className="settings-action-btn"
                  onClick={
                    compare
                      ? endCompare
                      : () => {
                          detachDiffModels();
                          setMode('edit');
                        }
                  }
                  disabled={saving}
                >
                  {compare
                    ? t('fileEdit.exitCompare', 'Exit compare')
                    : t('fileEdit.backToEditor', 'Back to editor')}
                </button>
              </>
            )}
            <button
              type="button"
              className="file-edit-close-btn"
              onClick={requestClose}
              aria-label={t('common.close')}
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Conflict bar */}
        {conflict && (
          <div className="file-edit-bar file-edit-bar--warn">
            <AlertTriangle size={13} />
            <span>
              {local
                ? t('fileEdit.localChanged', 'This file changed on disk after you opened it.')
                : t('fileEdit.remoteChanged', 'This file changed on the server after you opened it.')}
            </span>
            <button type="button" className="ftp-mini-btn" onClick={requestOpenDiff}>
              {t('fileEdit.viewDiff', 'View diff')}
            </button>
            <button
              type="button"
              className="ftp-mini-btn ftp-mini-btn--danger"
              onClick={() => doWrite(contentRef.current, null)}
            >
              {t('fileEdit.overwrite', 'Overwrite')}
            </button>
            <button type="button" className="ftp-mini-btn" onClick={() => setConflict(false)}>
              {t('common.cancel')}
            </button>
          </div>
        )}

        {/* Portalled to <body>: `.modal-content` keeps a transform from its open
            animation, which would make the confirm's fixed overlay position
            against the editor box instead of the window. */}
        {confirmDropLeft &&
          createPortal(
        <ConfirmModal
          isOpen={confirmDropLeft}
          onClose={() => setConfirmDropLeft(false)}
          onConfirm={openDiff}
          type="warning"
          title={t('fileEdit.dropLeftTitle', 'Replace the edited left side?')}
          message={t(
            'fileEdit.dropLeftBody',
            'The diff shows the server version on the left. Your edits to the left-hand scratch copy are not saved anywhere and will be lost. Use Copy first to keep them.'
          )}
          confirmText={t('fileEdit.dropLeftConfirm', 'Show server version')}
          cancelText={t('common.cancel')}
        />,
            document.body
          )}

        {/* Discard-changes guard */}
        {confirmDiscard && (
          <div className="file-edit-bar file-edit-bar--warn">
            <AlertTriangle size={13} />
            <span>{t('fileEdit.discardAsk', 'Discard unsaved changes?')}</span>
            <button
              type="button"
              className="ftp-mini-btn ftp-mini-btn--danger"
              onClick={() => {
                detachDiffModels();
                onClose();
              }}
            >
              {t('fileEdit.discard', 'Discard')}
            </button>
            <button type="button" className="ftp-mini-btn" onClick={() => setConfirmDiscard(false)}>
              {t('fileEdit.keepEditing', 'Keep editing')}
            </button>
          </div>
        )}

        {saveError && (
          <div className="file-edit-bar file-edit-bar--error file-edit-bar--lines" role="alert" data-testid="file-edit-save-error">
            {saveError}
          </div>
        )}
        {compareError && (
          <div className="file-edit-bar file-edit-bar--error">{compareError}</div>
        )}

        {/* Body */}
        <div className="file-edit-body">
          {loading ? (
            <div className="file-edit-center">
              <LoaderCircle size={18} className="animate-spin" />
            </div>
          ) : loadError ? (
            <div className="file-edit-center file-edit-center--error">{loadError}</div>
          ) : mode === 'diff' ? (
            <div className="file-edit-diff-wrap">
              {/* Which side is which. Two equal columns matching Monaco's 50/50
                  split — the sash is locked below (enableSplitViewResizing:
                  false) precisely so these headers cannot drift out of
                  alignment with the panes they label. */}
              <div className="file-edit-diff-heads">
                <div className="file-edit-diff-head file-edit-diff-head--left">
                  <span className="file-edit-diff-side">
                    {t('fileEdit.sideLeft', 'Left')}
                  </span>
                  <span className="file-edit-diff-what">
                    {compare
                      ? compare.label === 'clipboard'
                        ? t('fileEdit.headClipboard', 'Clipboard')
                        : compare.name
                      : t('fileEdit.headServer', 'Server version')}
                  </span>
                  <span className="file-edit-diff-note">
                    {compare
                      ? compareDirty
                        ? t('fileEdit.scratchEdited', 'scratch copy — edited, not saved anywhere')
                        : t('fileEdit.scratch', 'scratch copy — never written to disk')
                      : t('fileEdit.readOnly', 'read-only')}
                  </span>
                  {compare && (
                    <button
                      type="button"
                      className="file-edit-copy-btn"
                      onClick={copyCompareResult}
                      title={t('fileEdit.copyLeft', 'Copy this side to the clipboard')}
                    >
                      {copied ? <Check size={11} /> : <ClipboardCopy size={11} />}
                      {copied ? t('fileEdit.copied', 'Copied') : t('fileEdit.copy', 'Copy')}
                    </button>
                  )}
                </div>
                <div className="file-edit-diff-head file-edit-diff-head--right">
                  <span className="file-edit-diff-side">
                    {t('fileEdit.sideRight', 'Right')}
                  </span>
                  <span className="file-edit-diff-what">
                    {name} — {t('fileEdit.headYours', 'your version')}
                  </span>
                  <span className="file-edit-diff-note">
                    {local
                      ? t('fileEdit.editableLocal', 'editable, saves to this computer')
                      : t('fileEdit.editable', 'editable, saves to the server')}
                  </span>
                </div>
              </div>

              {/* Merge buttons. Rendered as an overlay column between the panes
                  rather than as Monaco view zones: a view zone would push the
                  lines apart and break the alignment the diff exists to show.
                  Positions come from the editor's own line positions, so they
                  track scrolling and folding for free. */}
              <div className="file-edit-diff-pane">
              {compare && hunks.length > 0 && (
                <div className="file-edit-merge-rail" aria-hidden={false}>
                  {hunks.map((h) => (
                    <div
                      key={`${h.change.originalStartLineNumber}-${h.change.modifiedStartLineNumber}`}
                      className="file-edit-merge-pair"
                      style={{ top: h.top }}
                    >
                      <button
                        type="button"
                        className="file-edit-merge-btn"
                        title={t('fileEdit.applyHunk', 'Copy this change into your version')}
                        aria-label={t('fileEdit.applyHunk', 'Copy this change into your version')}
                        onClick={() => applyHunk(h.change)}
                      >
                        <ArrowRight size={12} />
                      </button>
                      <button
                        type="button"
                        className="file-edit-merge-btn"
                        title={t('fileEdit.applyHunkBack', 'Copy your version into the left side')}
                        aria-label={t('fileEdit.applyHunkBack', 'Copy your version into the left side')}
                        onClick={() => applyHunkReverseAction(h.change)}
                      >
                        <ArrowLeft size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

            <DiffEditor
              original={compare ? compareContent : latest?.content ?? ''}
              modified={content}
              language={language}
              theme={monacoTheme}
              beforeMount={(monaco) => registerMonacoThemes(monaco)}
              onMount={(editor, monaco) => {
                const modified = editor.getModifiedEditor();
                diffModifiedRef.current = modified;
                diffEditorRef.current = editor;
                modified.updateOptions({ readOnly: false });
                // Keep the right side synced into state so "Back to editor"
                // carries the merge along instead of dropping it.
                modified.onDidChangeModelContent(() => setContent(modified.getValue()));
                modified.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () =>
                  saveRef.current()
                );

                // Place one merge button per hunk, vertically aligned with the
                // line it applies to.
                //
                // `getTopForLineNumber` is asked of the MODIFIED editor because
                // that is the side the button writes to, and it already accounts
                // for scroll position and any folded regions. A pure insertion
                // has no line on this side, so its anchor is the line it will be
                // inserted BEFORE — clamped to 1 so an insert at the very top
                // does not compute a negative position.
                const recomputeHunks = () => {
                  const changes = editor.getLineChanges() || [];
                  const scrollTop = modified.getScrollTop();
                  setHunks(
                    changes.map((change) => {
                      const anchor = Math.max(1, change.modifiedStartLineNumber);
                      return { change, top: modified.getTopForLineNumber(anchor) - scrollTop };
                    })
                  );
                };
                editor.onDidUpdateDiff(recomputeHunks);
                modified.onDidScrollChange(recomputeHunks);
                recomputeHunks();
              }}
              options={{
                ...editorOptions,
                renderSideBySide: true,
                originalEditable: false,
                // Locked so the header columns above stay aligned with the panes.
                enableSplitViewResizing: false,
              }}
            />
              </div>
            </div>
          ) : (
            <Editor
              value={content}
              language={language}
              theme={monacoTheme}
              onChange={(v) => setContent(v ?? '')}
              beforeMount={(monaco) => registerMonacoThemes(monaco)}
              onMount={bindSaveShortcut}
              options={editorOptions}
            />
          )}
        </div>
      </div>
    </div>
  );
}
